import { useLayoutEffect, useState } from 'react';

/**
 * A custom hook to detect changes in the window object's media query.
 * @param {string} query - Media query string.
 * @returns {boolean} - Current match status of the media query.
 */

interface MediaQueryState {
  mediaQuery: MediaQueryList;
  listeners: Set<(matches: boolean) => void>;
  handler: (event: MediaQueryListEvent | MediaQueryList) => void;
  waiting: boolean;
  rafId: number;
}

const mediaQueryStates = new Map<string, MediaQueryState>();

const getMediaQuery = (query: string): MediaQueryList => {
  if (mediaQueryStates.has(query)) {
    return mediaQueryStates.get(query)!.mediaQuery;
  }

  const mediaQuery = window.matchMedia(query);
  const handler = (event: MediaQueryListEvent | MediaQueryList): void => {
    const currentState = mediaQueryStates.get(query);
    if (currentState && !currentState.waiting) {
      currentState.waiting = true;
      currentState.rafId = requestAnimationFrame(() => {
        currentState.waiting = false;
        currentState.rafId = 0;

        currentState.listeners.forEach((listener) => listener(event.matches));
      });
    }
  };
  mediaQueryStates.set(query, {
    mediaQuery,
    listeners: new Set(),
    handler,
    waiting: false,
    rafId: 0,
  });

  if (mediaQuery.addEventListener) {
    mediaQuery.addEventListener('change', handler, { passive: true });
  } else {
    mediaQuery.addListener(handler);
  }
  return mediaQuery;
};

const addMediaQueryCallback = (query: string, callback: (matches: boolean) => void): void => {
  const state = mediaQueryStates.get(query);
  if (!state) return;

  state.listeners.add(callback);
};

const removeMediaQueryCallback = (query: string, callback: (matches: boolean) => void): void => {
  const state = mediaQueryStates.get(query);
  if (!state) return;

  state.listeners.delete(callback);

  if (state.listeners.size === 0) {
    // cancle any pending raf
    if (state.rafId) {
      cancelAnimationFrame(state.rafId);
    }

    // cleanup the handler
    if (state.mediaQuery.removeEventListener) {
      state.mediaQuery.removeEventListener('change', state.handler);
    } else {
      state.mediaQuery.removeListener(state.handler);
    }
    mediaQueryStates.delete(query);
  }
};

export const useMedia = (query: string): boolean => {
  const [matches, setMatches] = useState(() => getMediaQuery(query).matches);

  useLayoutEffect(() => {
    addMediaQueryCallback(query, setMatches);

    return (): void => {
      removeMediaQueryCallback(query, setMatches);
    };
  }, [query]);

  return matches;
};
